import React, { useState, useEffect } from 'react';
import { Shield, Lock, Download, Eye, Plus, Trash2, FileText, Menu, X, Heart, Sparkles } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ATSChecker from './components/ATSChecker';
import AuthModal from './components/AuthModal';
import PricingPage from './components/PricingPage';
import AIAnalyzer from './components/AIAnalyzer';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const generateId = () => Math.random().toString(36).substr(2, 9);

const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error('Storage error:', e);
    return false;
  }
};

const loadFromLocalStorage = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

// ============================================================================
// DEFAULT RESUME DATA STRUCTURE
// ============================================================================

const defaultResume = {
  personal: {
    name: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    website: ''
  },
  summary: '',
  experience: [],
  education: [],
  skills: [],
  projects: [],
  certifications: []
};

// ============================================================================
// NAVIGATION COMPONENT
// ============================================================================

const Navigation = ({ currentPage, onNavigate, isMobile, hasProSubscription }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const allNavLinks = [
    { id: 'home', label: 'Home' },
    { id: 'builder', label: 'Builder' },
    { id: 'ats-checker', label: 'ATS Checker' },
    { id: 'ai-analyzer', label: 'AI Analyzer', pro: true },
    { id: 'pricing', label: 'Pricing', hideForPro: true },
    { id: 'templates', label: 'Templates' },
    { id: 'about', label: 'About' },
    { id: 'faq', label: 'FAQ' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'terms', label: 'Terms' }
  ];

  // Filter nav links based on PRO status
  const navLinks = allNavLinks.filter(link => {
    if (link.hideForPro && hasProSubscription) {
      return false; // Hide pricing for PRO users
    }
    return true;
  });

  const handleNavigate = (page) => {
    onNavigate(page);
    setMobileMenuOpen(false);
  };

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(20, 90, 100, 0.08)',
      zIndex: 1000,
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '16px 20px' : '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer'
        }} onClick={() => handleNavigate('home')}>
          <div style={{
            width: '36px',
            height: '36px',
            background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(20, 90, 100, 0.2)'
          }}>
            <Shield size={20} color="white" />
          </div>
          <span style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#145A64',
            letterSpacing: '-0.5px',
            fontFamily: '"DM Serif Display", serif'
          }}>
            theresumex
          </span>
        </div>

        {isMobile ? (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#145A64'
            }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => handleNavigate(link.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  fontWeight: currentPage === link.id ? '600' : '500',
                  color: currentPage === link.id ? '#145A64' : '#5A7C82',
                  cursor: 'pointer',
                  padding: '8px 0',
                  borderBottom: currentPage === link.id ? '2px solid #1A8A99' : '2px solid transparent',
                  transition: 'all 0.3s ease',
                  fontFamily: '"Inter", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {link.label}
                {link.pro && (
                  <span style={{
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}>
                    <Sparkles size={10} />
                    PRO
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {isMobile && mobileMenuOpen && (
        <div style={{
          background: 'white',
          borderTop: '1px solid rgba(20, 90, 100, 0.08)',
          animation: 'slideDown 0.3s ease'
        }}>
          {navLinks.map(link => (
            <button
              key={link.id}
              onClick={() => handleNavigate(link.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                textAlign: 'left',
                background: currentPage === link.id ? 'rgba(20, 90, 100, 0.05)' : 'none',
                border: 'none',
                padding: '16px 20px',
                fontSize: '15px',
                fontWeight: currentPage === link.id ? '600' : '500',
                color: currentPage === link.id ? '#145A64' : '#5A7C82',
                cursor: 'pointer',
                borderLeft: currentPage === link.id ? '3px solid #1A8A99' : '3px solid transparent',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              {link.label}
              {link.pro && (
                <span style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  <Sparkles size={10} />
                  PRO
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
};

// ============================================================================
// FOOTER COMPONENT
// ============================================================================

const Footer = ({ onNavigate, isMobile }) => {
  return (
    <footer style={{
      background: 'linear-gradient(180deg, #F8FAFB 0%, #EDF2F4 100%)',
      borderTop: '1px solid rgba(20, 90, 100, 0.08)',
      padding: isMobile ? '40px 20px' : '60px 40px',
      marginTop: '80px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? '40px' : '60px',
          marginBottom: '40px'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Shield size={18} color="white" />
              </div>
              <span style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#145A64',
                fontFamily: '"DM Serif Display", serif'
              }}>
                theresumex
              </span>
            </div>
            <p style={{
              fontSize: '14px',
              color: '#5A7C82',
              lineHeight: '1.6',
              margin: '0 0 16px'
            }}>
              Privacy-first resume builder with AI-powered enhancements. All your data stays on your device.
            </p>
            <p style={{
              fontSize: '13px',
              color: '#7A9CA3',
              lineHeight: '1.5',
              margin: '0'
            }}>
              Contact: <a href="mailto:info.theresumex@gmail.com" style={{ color: '#145A64', textDecoration: 'none' }}>info.theresumex@gmail.com</a>
            </p>
          </div>

          <div>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Navigation
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['home', 'builder', 'ats-checker', 'ai-analyzer', 'templates', 'pricing', 'about'].map(page => (
                <button
                  key={page}
                  onClick={() => onNavigate(page)}
                  style={{
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    color: '#5A7C82',
                    cursor: 'pointer',
                    padding: '0',
                    transition: 'color 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#145A64'}
                  onMouseLeave={(e) => e.target.style.color = '#5A7C82'}
                >
                  {page.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  {page === 'ai-analyzer' && <span style={{ fontSize: '10px', color: '#8B5CF6', fontWeight: '700' }}>PRO</span>}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Legal & Support
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['privacy', 'terms', 'faq'].map(page => (
                <button
                  key={page}
                  onClick={() => onNavigate(page)}
                  style={{
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    color: '#5A7C82',
                    cursor: 'pointer',
                    padding: '0',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#145A64'}
                  onMouseLeave={(e) => e.target.style.color = '#5A7C82'}
                >
                  {page === 'faq' ? 'FAQ' : page.charAt(0).toUpperCase() + page.slice(1)}
                </button>
              ))}
              <div style={{ fontSize: '13px', color: '#5A7C82', marginTop: '8px', lineHeight: '1.6' }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Refund Policy:</div>
                <div style={{ fontSize: '12px', color: '#7A9CA3' }}>
                  ✅ Refunds may be considered if:<br/>
                  • Service not received<br/>
                  • Technical issue prevented use<br/>
                  • Duplicate charges<br/>
                  Each case reviewed individually.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{
          borderTop: '1px solid rgba(20, 90, 100, 0.08)',
          paddingTop: '24px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '16px'
        }}>
          <p style={{
            fontSize: '13px',
            color: '#7A9CA3',
            margin: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Lock size={14} />
            <span>Privacy-first. Local-only. No uploads to any server.</span>
          </p>
          <p style={{
            fontSize: '13px',
            color: '#7A9CA3',
            margin: '0'
          }}>
            © 2026 theresumex. Built with care for your privacy.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// HOME PAGE
// ============================================================================

const HomePage = ({ onNavigate, isMobile }) => {
  return (
    <div style={{ animation: 'fadeIn 0.6s ease' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #F0F9FA 0%, #E8F4F5 100%)',
        padding: isMobile ? '60px 20px' : '120px 40px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(26, 138, 153, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(20, 90, 100, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(20, 90, 100, 0.08)',
            padding: '8px 16px',
            borderRadius: '20px',
            marginBottom: '24px',
            animation: 'slideDown 0.6s ease 0.2s both'
          }}>
            <Lock size={14} color="#145A64" />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#145A64',
              letterSpacing: '0.3px'
            }}>
              100% PRIVATE & SECURE
            </span>
          </div>

          <h1 style={{
            fontSize: isMobile ? '42px' : '72px',
            fontWeight: '700',
            color: '#0D3B42',
            margin: '0 0 24px 0',
            lineHeight: '1.1',
            fontFamily: '"DM Serif Display", serif',
            letterSpacing: '-1px',
            animation: 'slideUp 0.6s ease 0.3s both'
          }}>
            Build Your Resume.<br />Keep Your Data.
          </h1>

          <p style={{
            fontSize: isMobile ? '18px' : '22px',
            color: '#5A7C82',
            lineHeight: '1.6',
            maxWidth: '700px',
            margin: '0 auto 40px auto',
            animation: 'slideUp 0.6s ease 0.4s both'
          }}>
            No uploads to any server. No accounts. No cloud storage. Everything stays on your device, forever.
          </p>

          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            animation: 'slideUp 0.6s ease 0.5s both'
          }}>
            <button
              onClick={() => onNavigate('builder')}
              style={{
                background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
                color: 'white',
                border: 'none',
                padding: isMobile ? '16px 32px' : '18px 40px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(20, 90, 100, 0.25)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 32px rgba(20, 90, 100, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 24px rgba(20, 90, 100, 0.25)';
              }}
            >
              Start Building
              <FileText size={18} />
            </button>

            <button
              onClick={() => onNavigate('templates')}
              style={{
                background: 'white',
                color: '#145A64',
                border: '2px solid #145A64',
                padding: isMobile ? '16px 32px' : '18px 40px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#145A64';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#145A64';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              View Templates
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: isMobile ? '36px' : '48px',
          fontWeight: '700',
          color: '#0D3B42',
          textAlign: 'center',
          marginBottom: '16px',
          fontFamily: '"DM Serif Display", serif'
        }}>
          How It Works
        </h2>
        <p style={{
          fontSize: '18px',
          color: '#5A7C82',
          textAlign: 'center',
          marginBottom: '60px',
          maxWidth: '600px',
          margin: '0 auto 60px auto'
        }}>
          Three simple steps to create your perfect resume
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '40px'
        }}>
          {[
            {
              number: '01',
              title: 'Enter Your Details',
              description: 'Fill in your information directly in your browser. Nothing is uploaded.',
              icon: <FileText size={32} />
            },
            {
              number: '02',
              title: 'Choose a Template',
              description: 'Select from professional templates. Preview in real-time.',
              icon: <Eye size={32} />
            },
            {
              number: '03',
              title: 'Download Locally',
              description: 'Generate your PDF right in your browser. No server involved.',
              icon: <Download size={32} />
            }
          ].map((step, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                padding: '40px',
                borderRadius: '16px',
                border: '1px solid rgba(20, 90, 100, 0.08)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease',
                animation: `slideUp 0.6s ease ${0.2 + index * 0.1}s both`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(20, 90, 100, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
              }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(20, 90, 100, 0.1) 0%, rgba(26, 138, 153, 0.15) 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                color: '#145A64'
              }}>
                {step.icon}
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#1A8A99',
                letterSpacing: '1px',
                marginBottom: '12px'
              }}>
                STEP {step.number}
              </div>
              <h3 style={{
                fontSize: '22px',
                fontWeight: '600',
                color: '#0D3B42',
                margin: '0 0 12px 0'
              }}>
                {step.title}
              </h3>
              <p style={{
                fontSize: '15px',
                color: '#5A7C82',
                lineHeight: '1.6',
                margin: '0'
              }}>
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ATS Checker CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
        padding: isMobile ? '80px 20px' : '120px 40px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: '250px',
          height: '250px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          filter: 'blur(70px)'
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            fontSize: isMobile ? '48px' : '64px',
            marginBottom: '24px'
          }}>
            ✓
          </div>
          <h2 style={{
            fontSize: isMobile ? '36px' : '52px',
            fontWeight: '700',
            color: 'white',
            marginBottom: '20px',
            fontFamily: '"DM Serif Display", serif',
            lineHeight: '1.2'
          }}>
            Check Your Resume's ATS Score
          </h2>
          <p style={{
            fontSize: isMobile ? '18px' : '22px',
            color: 'rgba(255, 255, 255, 0.95)',
            lineHeight: '1.6',
            maxWidth: '700px',
            margin: '0 auto 40px auto'
          }}>
            Get an instant ATS compatibility score and actionable suggestions to improve your resume. 100% private, no uploads to any server required.
          </p>
          <button
            onClick={() => onNavigate('ats-checker')}
            style={{
              background: 'white',
              color: '#059669',
              border: 'none',
              padding: isMobile ? '18px 36px' : '20px 48px',
              fontSize: '18px',
              fontWeight: '700',
              borderRadius: '12px',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.boxShadow = '0 12px 32px rgba(0,0,0,0.25)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
            }}
          >
            Check ATS Score Now
            <span style={{ fontSize: '24px' }}>→</span>
          </button>
          <div style={{
            marginTop: '32px',
            display: 'flex',
            gap: '32px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: '500'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={16} />
              <span>Privacy-First</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Lock size={16} />
              <span>No AI Used</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={16} />
              <span>Instant Results</span>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Assurance */}
      <section style={{
        background: 'linear-gradient(135deg, #0D3B42 0%, #145A64 100%)',
        padding: isMobile ? '60px 20px' : '100px 40px',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '36px' : '48px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '16px',
            fontFamily: '"DM Serif Display", serif'
          }}>
            Your Privacy Guaranteed
          </h2>
          <p style={{
            fontSize: '18px',
            textAlign: 'center',
            marginBottom: '60px',
            opacity: '0.9',
            maxWidth: '600px',
            margin: '0 auto 60px auto'
          }}>
            We built theresumex with one mission: keep your data yours
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '32px'
          }}>
            {[
              {
                icon: <Lock size={28} />,
                title: 'No Cloud Storage',
                description: 'Your resume never touches our servers. Ever.'
              },
              {
                icon: <Shield size={28} />,
                title: 'No Accounts Needed',
                description: 'Start building immediately. No signup, no login.'
              },
              {
                icon: <Eye size={28} />,
                title: 'We Can\'t See It',
                description: 'Your data stays in your browser. We have zero access.'
              }
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  textAlign: 'center',
                  animation: `fadeIn 0.6s ease ${0.3 + index * 0.1}s both`
                }}
              >
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px auto',
                  backdropFilter: 'blur(10px)'
                }}>
                  {item.icon}
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  margin: '0 0 12px 0'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  opacity: '0.85',
                  lineHeight: '1.6',
                  margin: '0'
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Template Preview */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: isMobile ? '36px' : '48px',
          fontWeight: '700',
          color: '#0D3B42',
          textAlign: 'center',
          marginBottom: '16px',
          fontFamily: '"DM Serif Display", serif'
        }}>
          Professional Templates
        </h2>
        <p style={{
          fontSize: '18px',
          color: '#5A7C82',
          textAlign: 'center',
          marginBottom: '60px',
          maxWidth: '600px',
          margin: '0 auto 60px auto'
        }}>
          Choose from carefully crafted templates designed for impact
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '32px',
          marginBottom: '40px'
        }}>
          {['Modern', 'Classic', 'Minimal'].map((template, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(20, 90, 100, 0.08)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: `slideUp 0.6s ease ${0.2 + index * 0.1}s both`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(20, 90, 100, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
              }}
              onClick={() => onNavigate('templates')}
            >
              <div style={{
                background: index === 0 ? 'linear-gradient(135deg, #F0F9FA 0%, #E8F4F5 100%)' :
                           index === 1 ? 'linear-gradient(135deg, #FFF9F0 0%, #FFF4E8 100%)' :
                           'linear-gradient(135deg, #F5F5F5 0%, #ECECEC 100%)',
                height: '280px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px'
              }}>
                <div style={{
                  background: 'white',
                  width: '100%',
                  height: '100%',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  padding: '16px',
                  fontSize: '10px',
                  lineHeight: '1.4',
                  color: '#666'
                }}>
                  <div style={{
                    fontWeight: '700',
                    fontSize: '14px',
                    color: '#0D3B42',
                    marginBottom: '8px'
                  }}>
                    {template} Template
                  </div>
                  <div style={{ height: '2px', background: '#1A8A99', width: '40px', marginBottom: '12px' }} />
                  <div style={{ marginBottom: '8px' }}>Professional Summary</div>
                  <div style={{ marginBottom: '8px' }}>Work Experience</div>
                  <div>Education & Skills</div>
                </div>
              </div>
              <div style={{ padding: '20px' }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#0D3B42',
                  margin: '0 0 8px 0'
                }}>
                  {template}
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#5A7C82',
                  margin: '0'
                }}>
                  Perfect for {index === 0 ? 'tech professionals' : index === 1 ? 'traditional industries' : 'creative roles'}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => onNavigate('templates')}
            style={{
              background: 'none',
              color: '#145A64',
              border: '2px solid #145A64',
              padding: '14px 32px',
              fontSize: '15px',
              fontWeight: '600',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#145A64';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#145A64';
            }}
          >
            View All Templates
          </button>
        </div>
      </section>
    </div>
  );
};

// ============================================================================
// RESUME BUILDER PAGE
// ============================================================================

const BuilderPage = ({ isMobile }) => {
  const [resume, setResume] = useState(() => loadFromLocalStorage('resume', defaultResume));
  const [showPreview, setShowPreview] = useState(!isMobile);
  const [activeSection, setActiveSection] = useState('personal');

  useEffect(() => {
    saveToLocalStorage('resume', resume);
  }, [resume]);

  const updatePersonal = (field, value) => {
    setResume(prev => ({
      ...prev,
      personal: { ...prev.personal, [field]: value }
    }));
  };

  const addItem = (section) => {
    const newItem = section === 'experience' ? {
      id: generateId(),
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      description: ''
    } : section === 'education' ? {
      id: generateId(),
      degree: '',
      institution: '',
      location: '',
      graduationDate: '',
      gpa: ''
    } : section === 'projects' ? {
      id: generateId(),
      name: '',
      description: '',
      technologies: '',
      link: ''
    } : {
      id: generateId(),
      name: '',
      issuer: '',
      date: ''
    };

    setResume(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
  };

  const updateItem = (section, id, field, value) => {
    setResume(prev => ({
      ...prev,
      [section]: prev[section].map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (section, id) => {
    setResume(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.id !== id)
    }));
  };

  const addSkill = () => {
    const skillInput = window.prompt('Enter skill:');
    if (skillInput) {
      setResume(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput]
      }));
    }
  };

  const removeSkill = (index) => {
    setResume(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const clearResume = () => {
    if (window.confirm('Are you sure you want to clear all resume data? This cannot be undone.')) {
      setResume(defaultResume);
      localStorage.removeItem('resume');
    }
  };

  const exportPDF = async () => {
    try {
      const element = document.getElementById('resume-preview');
      if (!element) {
        alert('Please add some content to your resume first!');
        return;
      }

      // Show loading state
      const exportButton = document.querySelector('.export-pdf-btn');
      const originalButtonText = exportButton.textContent;
      exportButton.textContent = 'Generating High-Quality PDF...';
      exportButton.disabled = true;

      // Store original styles
      const originalStyles = {
        maxHeight: element.style.maxHeight,
        overflow: element.style.overflow,
        width: element.style.width,
        padding: element.style.padding,
        fontSize: element.style.fontSize,
        lineHeight: element.style.lineHeight
      };
      
      // A4 dimensions at 96 DPI (standard screen DPI)
      const A4_WIDTH_PX = 794;  // 210mm at 96 DPI
      const MARGIN_PX = 50;     // ~13mm margins
      
      // Set element to exact A4 size with proper styling
      element.style.width = `${A4_WIDTH_PX}px`;
      element.style.padding = `${MARGIN_PX}px`;
      element.style.maxHeight = 'none';
      element.style.overflow = 'visible';
      element.style.fontSize = '13px';
      element.style.lineHeight = '1.6';
      element.style.boxSizing = 'border-box';

      // Wait for layout to stabilize
      await new Promise(resolve => setTimeout(resolve, 300));

      // Capture at very high quality (3x scale for crisp text)
      const canvas = await html2canvas(element, {
        scale: 3,  // High quality - 3x resolution
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (clonedDoc) => {
          // Ensure fonts are loaded in cloned document
          const clonedElement = clonedDoc.getElementById('resume-preview');
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
          }
        }
      });

      // Restore original styles
      Object.keys(originalStyles).forEach(key => {
        element.style[key] = originalStyles[key];
      });

      // Create PDF with A4 dimensions (in points, standard PDF unit)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
        compress: false  // Don't compress for maximum quality
      });

      const pageWidth = pdf.internal.pageSize.getWidth();   // 595.28 pt
      const pageHeight = pdf.internal.pageSize.getHeight(); // 841.89 pt
      
      // Calculate image dimensions to fit A4 page width
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      
      // Determine if we need multiple pages
      if (imgHeight <= pageHeight) {
        // Single page - fits perfectly
        const imgData = canvas.toDataURL('image/png', 1.0);  // PNG for lossless quality
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        // Multiple pages needed
        const totalPages = Math.ceil(imgHeight / pageHeight);
        const canvasHeightPerPage = canvas.height / totalPages;
        
        for (let pageNum = 0; pageNum < totalPages; pageNum++) {
          if (pageNum > 0) {
            pdf.addPage();
          }

          // Calculate the portion of canvas for this page
          const sourceY = pageNum * canvasHeightPerPage;
          const sourceHeight = Math.min(
            canvasHeightPerPage,
            canvas.height - sourceY
          );

          // Create a canvas for this page
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sourceHeight;
          
          const ctx = pageCanvas.getContext('2d', { alpha: false });
          
          // White background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          
          // Draw the portion of content for this page
          ctx.drawImage(
            canvas,
            0, sourceY,           // Source position
            canvas.width, sourceHeight,  // Source dimensions
            0, 0,                 // Destination position
            canvas.width, sourceHeight   // Destination dimensions
          );

          // Convert to high-quality PNG
          const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
          
          // Calculate scaled height for this page
          const pageImgHeight = (sourceHeight * pageWidth) / canvas.width;
          
          // Add image to PDF with high quality
          pdf.addImage(pageImgData, 'PNG', 0, 0, imgWidth, pageImgHeight, undefined, 'FAST');
        }
      }

      // Generate filename
      const fileName = resume.personal.name 
        ? `${resume.personal.name.replace(/\s+/g, '_')}_Resume.pdf`
        : 'My_Resume.pdf';

      // Save PDF
      pdf.save(fileName);

      // Success message
      setTimeout(() => {
        exportButton.textContent = originalButtonText;
        exportButton.disabled = false;
        alert('✅ High-quality resume downloaded successfully!\n\n📄 Your PDF was generated at 3x resolution for maximum clarity.\n🔒 All processing happened locally - no data was uploaded.');
      }, 500);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('❌ Error generating PDF. Please try again.\n\nTip: If the resume is very long, try reducing some content or wait a moment and retry.');
      const exportButton = document.querySelector('.export-pdf-btn');
      if (exportButton) {
        exportButton.textContent = 'Export PDF';
        exportButton.disabled = false;
      }
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid rgba(20, 90, 100, 0.15)',
    borderRadius: '8px',
    fontFamily: 'inherit',
    transition: 'all 0.3s ease',
    background: 'white'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#145A64',
    marginBottom: '8px',
    letterSpacing: '0.3px'
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 200px)',
      background: '#F8FAFB',
      padding: isMobile ? '20px' : '40px'
    }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{
              fontSize: isMobile ? '28px' : '36px',
              fontWeight: '700',
              color: '#0D3B42',
              margin: '0 0 8px 0',
              fontFamily: '"DM Serif Display", serif'
            }}>
              Resume Builder
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#5A7C82',
              margin: '0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Lock size={14} />
              <span>Auto-saved locally on your device</span>
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {isMobile && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                style={{
                  background: 'white',
                  color: '#145A64',
                  border: '2px solid #145A64',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Eye size={16} />
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            )}

            <button
              onClick={exportPDF}
              className="export-pdf-btn"
              style={{
                background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(20, 90, 100, 0.2)'
              }}
            >
              <Download size={16} />
              Export PDF
            </button>

            <button
              onClick={clearResume}
              style={{
                background: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FCA5A5',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Trash2 size={16} />
              Clear
            </button>
          </div>
        </div>

        {/* Main Builder Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : (showPreview ? '1fr 1fr' : '1fr'),
          gap: '32px',
          alignItems: 'start'
        }}>
          {/* Editor Panel */}
          {(!isMobile || !showPreview) && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid rgba(20, 90, 100, 0.08)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
              overflow: 'hidden'
            }}>
              {/* Section Tabs */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                borderBottom: '1px solid rgba(20, 90, 100, 0.08)',
                background: '#F8FAFB'
              }}>
                {['personal', 'summary', 'experience', 'education', 'skills', 'projects'].map(section => (
                  <button
                    key={section}
                    onClick={() => setActiveSection(section)}
                    style={{
                      flex: isMobile ? '1 1 50%' : '1',
                      padding: '16px 12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: activeSection === section ? '#145A64' : '#5A7C82',
                      background: activeSection === section ? 'white' : 'transparent',
                      border: 'none',
                      borderBottom: activeSection === section ? '2px solid #1A8A99' : '2px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'capitalize'
                    }}
                  >
                    {section}
                  </button>
                ))}
              </div>

              {/* Form Content */}
              <div style={{ padding: isMobile ? '24px' : '32px', maxHeight: '70vh', overflowY: 'auto' }}>
                {activeSection === 'personal' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <label style={labelStyle}>Full Name *</label>
                      <input
                        type="text"
                        value={resume.personal.name}
                        onChange={(e) => updatePersonal('name', e.target.value)}
                        style={inputStyle}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Professional Title</label>
                      <input
                        type="text"
                        value={resume.personal.title}
                        onChange={(e) => updatePersonal('title', e.target.value)}
                        style={inputStyle}
                        placeholder="Senior Software Engineer"
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                      <div>
                        <label style={labelStyle}>Email *</label>
                        <input
                          type="email"
                          value={resume.personal.email}
                          onChange={(e) => updatePersonal('email', e.target.value)}
                          style={inputStyle}
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Phone</label>
                        <input
                          type="tel"
                          value={resume.personal.phone}
                          onChange={(e) => updatePersonal('phone', e.target.value)}
                          style={inputStyle}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Location</label>
                      <input
                        type="text"
                        value={resume.personal.location}
                        onChange={(e) => updatePersonal('location', e.target.value)}
                        style={inputStyle}
                        placeholder="San Francisco, CA"
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                      <div>
                        <label style={labelStyle}>LinkedIn</label>
                        <input
                          type="url"
                          value={resume.personal.linkedin}
                          onChange={(e) => updatePersonal('linkedin', e.target.value)}
                          style={inputStyle}
                          placeholder="linkedin.com/in/johndoe"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Website/Portfolio</label>
                        <input
                          type="url"
                          value={resume.personal.website}
                          onChange={(e) => updatePersonal('website', e.target.value)}
                          style={inputStyle}
                          placeholder="johndoe.com"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'summary' && (
                  <div>
                    <label style={labelStyle}>Professional Summary</label>
                    <textarea
                      value={resume.summary}
                      onChange={(e) => setResume(prev => ({ ...prev, summary: e.target.value }))}
                      style={{ ...inputStyle, minHeight: '180px', resize: 'vertical' }}
                      placeholder="Write a brief summary highlighting your key qualifications, experience, and career goals..."
                    />
                    <p style={{ fontSize: '12px', color: '#7A9CA3', marginTop: '8px' }}>
                      Tip: Keep it concise (3-4 sentences) and focus on your unique value proposition
                    </p>
                  </div>
                )}

                {activeSection === 'experience' && (
                  <div>
                    {resume.experience.map((exp, index) => (
                      <div
                        key={exp.id}
                        style={{
                          marginBottom: '24px',
                          padding: '24px',
                          background: '#F8FAFB',
                          borderRadius: '8px',
                          border: '1px solid rgba(20, 90, 100, 0.08)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <h4 style={{ margin: '0', color: '#145A64', fontSize: '14px', fontWeight: '600' }}>
                            Experience #{index + 1}
                          </h4>
                          <button
                            onClick={() => removeItem('experience', exp.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <input
                            type="text"
                            value={exp.title}
                            onChange={(e) => updateItem('experience', exp.id, 'title', e.target.value)}
                            style={inputStyle}
                            placeholder="Job Title"
                          />
                          <input
                            type="text"
                            value={exp.company}
                            onChange={(e) => updateItem('experience', exp.id, 'company', e.target.value)}
                            style={inputStyle}
                            placeholder="Company Name"
                          />
                          <input
                            type="text"
                            value={exp.location}
                            onChange={(e) => updateItem('experience', exp.id, 'location', e.target.value)}
                            style={inputStyle}
                            placeholder="Location"
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                            <input
                              type="text"
                              value={exp.startDate}
                              onChange={(e) => updateItem('experience', exp.id, 'startDate', e.target.value)}
                              style={inputStyle}
                              placeholder="Start Date (e.g., Jan 2020)"
                            />
                            <input
                              type="text"
                              value={exp.endDate}
                              onChange={(e) => updateItem('experience', exp.id, 'endDate', e.target.value)}
                              style={inputStyle}
                              placeholder="End Date (e.g., Dec 2022)"
                              disabled={exp.current}
                            />
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                            <input
                              type="checkbox"
                              checked={exp.current}
                              onChange={(e) => updateItem('experience', exp.id, 'current', e.target.checked)}
                            />
                            Currently working here
                          </label>
                          <textarea
                            value={exp.description}
                            onChange={(e) => updateItem('experience', exp.id, 'description', e.target.value)}
                            style={{ ...inputStyle, minHeight: '120px' }}
                            placeholder="Describe your responsibilities and achievements..."
                          />
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => addItem('experience')}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'linear-gradient(135deg, rgba(20, 90, 100, 0.05) 0%, rgba(26, 138, 153, 0.08) 100%)',
                        border: '2px dashed rgba(20, 90, 100, 0.2)',
                        borderRadius: '8px',
                        color: '#145A64',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Plus size={18} />
                      Add Experience
                    </button>
                  </div>
                )}

                {activeSection === 'education' && (
                  <div>
                    {resume.education.map((edu, index) => (
                      <div
                        key={edu.id}
                        style={{
                          marginBottom: '24px',
                          padding: '24px',
                          background: '#F8FAFB',
                          borderRadius: '8px',
                          border: '1px solid rgba(20, 90, 100, 0.08)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <h4 style={{ margin: '0', color: '#145A64', fontSize: '14px', fontWeight: '600' }}>
                            Education #{index + 1}
                          </h4>
                          <button
                            onClick={() => removeItem('education', edu.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <input
                            type="text"
                            value={edu.degree}
                            onChange={(e) => updateItem('education', edu.id, 'degree', e.target.value)}
                            style={inputStyle}
                            placeholder="Degree (e.g., Bachelor of Science in Computer Science)"
                          />
                          <input
                            type="text"
                            value={edu.institution}
                            onChange={(e) => updateItem('education', edu.id, 'institution', e.target.value)}
                            style={inputStyle}
                            placeholder="Institution Name"
                          />
                          <input
                            type="text"
                            value={edu.location}
                            onChange={(e) => updateItem('education', edu.id, 'location', e.target.value)}
                            style={inputStyle}
                            placeholder="Location"
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                            <input
                              type="text"
                              value={edu.graduationDate}
                              onChange={(e) => updateItem('education', edu.id, 'graduationDate', e.target.value)}
                              style={inputStyle}
                              placeholder="Graduation Date"
                            />
                            <input
                              type="text"
                              value={edu.gpa}
                              onChange={(e) => updateItem('education', edu.id, 'gpa', e.target.value)}
                              style={inputStyle}
                              placeholder="GPA (optional)"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => addItem('education')}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'linear-gradient(135deg, rgba(20, 90, 100, 0.05) 0%, rgba(26, 138, 153, 0.08) 100%)',
                        border: '2px dashed rgba(20, 90, 100, 0.2)',
                        borderRadius: '8px',
                        color: '#145A64',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Plus size={18} />
                      Add Education
                    </button>
                  </div>
                )}

                {activeSection === 'skills' && (
                  <div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '12px',
                      marginBottom: '20px'
                    }}>
                      {resume.skills.map((skill, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, rgba(20, 90, 100, 0.08) 0%, rgba(26, 138, 153, 0.12) 100%)',
                            borderRadius: '20px',
                            fontSize: '14px',
                            color: '#145A64',
                            fontWeight: '500'
                          }}
                        >
                          {skill}
                          <button
                            onClick={() => removeSkill(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: 'pointer',
                              padding: '2px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addSkill}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'linear-gradient(135deg, rgba(20, 90, 100, 0.05) 0%, rgba(26, 138, 153, 0.08) 100%)',
                        border: '2px dashed rgba(20, 90, 100, 0.2)',
                        borderRadius: '8px',
                        color: '#145A64',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Plus size={18} />
                      Add Skill
                    </button>
                  </div>
                )}

                {activeSection === 'projects' && (
                  <div>
                    {resume.projects.map((proj, index) => (
                      <div
                        key={proj.id}
                        style={{
                          marginBottom: '24px',
                          padding: '24px',
                          background: '#F8FAFB',
                          borderRadius: '8px',
                          border: '1px solid rgba(20, 90, 100, 0.08)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <h4 style={{ margin: '0', color: '#145A64', fontSize: '14px', fontWeight: '600' }}>
                            Project #{index + 1}
                          </h4>
                          <button
                            onClick={() => removeItem('projects', proj.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <input
                            type="text"
                            value={proj.name}
                            onChange={(e) => updateItem('projects', proj.id, 'name', e.target.value)}
                            style={inputStyle}
                            placeholder="Project Name"
                          />
                          <textarea
                            value={proj.description}
                            onChange={(e) => updateItem('projects', proj.id, 'description', e.target.value)}
                            style={{ ...inputStyle, minHeight: '100px' }}
                            placeholder="Project description..."
                          />
                          <input
                            type="text"
                            value={proj.technologies}
                            onChange={(e) => updateItem('projects', proj.id, 'technologies', e.target.value)}
                            style={inputStyle}
                            placeholder="Technologies used (e.g., React, Node.js, MongoDB)"
                          />
                          <input
                            type="url"
                            value={proj.link}
                            onChange={(e) => updateItem('projects', proj.id, 'link', e.target.value)}
                            style={inputStyle}
                            placeholder="Project link (optional)"
                          />
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => addItem('projects')}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'linear-gradient(135deg, rgba(20, 90, 100, 0.05) 0%, rgba(26, 138, 153, 0.08) 100%)',
                        border: '2px dashed rgba(20, 90, 100, 0.2)',
                        borderRadius: '8px',
                        color: '#145A64',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Plus size={18} />
                      Add Project
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Preview Panel */}
          {(!isMobile || showPreview) && (
            <div style={{
              position: isMobile ? 'relative' : 'sticky',
              top: isMobile ? '0' : '100px'
            }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(20, 90, 100, 0.08)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 24px',
                  background: '#F8FAFB',
                  borderBottom: '1px solid rgba(20, 90, 100, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Eye size={18} color="#145A64" />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#145A64' }}>
                    Live Preview
                  </span>
                </div>

                <div 
                  id="resume-preview"
                  style={{
                  padding: '40px',
                  minHeight: '600px',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  fontSize: '11px',
                  lineHeight: '1.5',
                  color: '#333'
                }}>
                  {/* Resume Preview Content */}
                  {resume.personal.name && (
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      <h1 style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#0D3B42',
                        margin: '0 0 4px 0',
                        fontFamily: '"DM Serif Display", serif'
                      }}>
                        {resume.personal.name}
                      </h1>
                      {resume.personal.title && (
                        <p style={{
                          fontSize: '13px',
                          color: '#1A8A99',
                          margin: '0 0 12px 0',
                          fontWeight: '500'
                        }}>
                          {resume.personal.title}
                        </p>
                      )}
                      <div style={{
                        fontSize: '10px',
                        color: '#5A7C82',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        justifyContent: 'center'
                      }}>
                        {resume.personal.email && <span>{resume.personal.email}</span>}
                        {resume.personal.phone && <span>• {resume.personal.phone}</span>}
                        {resume.personal.location && <span>• {resume.personal.location}</span>}
                      </div>
                    </div>
                  )}

                  {resume.summary && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#145A64',
                        margin: '0 0 8px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #1A8A99',
                        paddingBottom: '4px'
                      }}>
                        Professional Summary
                      </h3>
                      <p style={{ margin: '0', fontSize: '10px', lineHeight: '1.6' }}>
                        {resume.summary}
                      </p>
                    </div>
                  )}

                  {resume.experience.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#145A64',
                        margin: '0 0 12px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #1A8A99',
                        paddingBottom: '4px'
                      }}>
                        Work Experience
                      </h3>
                      {resume.experience.map(exp => (
                        <div key={exp.id} style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <strong style={{ fontSize: '11px', color: '#0D3B42' }}>
                              {exp.title || 'Job Title'}
                            </strong>
                            <span style={{ fontSize: '9px', color: '#7A9CA3' }}>
                              {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#5A7C82', marginBottom: '6px' }}>
                            {exp.company} {exp.location && `• ${exp.location}`}
                          </div>
                          {exp.description && (
                            <p style={{ margin: '0', fontSize: '10px', lineHeight: '1.5' }}>
                              {exp.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {resume.education.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#145A64',
                        margin: '0 0 12px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #1A8A99',
                        paddingBottom: '4px'
                      }}>
                        Education
                      </h3>
                      {resume.education.map(edu => (
                        <div key={edu.id} style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <strong style={{ fontSize: '11px', color: '#0D3B42' }}>
                              {edu.degree || 'Degree'}
                            </strong>
                            <span style={{ fontSize: '9px', color: '#7A9CA3' }}>
                              {edu.graduationDate}
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#5A7C82' }}>
                            {edu.institution} {edu.location && `• ${edu.location}`}
                            {edu.gpa && ` • GPA: ${edu.gpa}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {resume.skills.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#145A64',
                        margin: '0 0 8px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #1A8A99',
                        paddingBottom: '4px'
                      }}>
                        Skills
                      </h3>
                      <div style={{ fontSize: '10px', lineHeight: '1.8' }}>
                        {resume.skills.join(' • ')}
                      </div>
                    </div>
                  )}

                  {resume.projects.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#145A64',
                        margin: '0 0 12px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #1A8A99',
                        paddingBottom: '4px'
                      }}>
                        Projects
                      </h3>
                      {resume.projects.map(proj => (
                        <div key={proj.id} style={{ marginBottom: '12px' }}>
                          <strong style={{ fontSize: '11px', color: '#0D3B42', display: 'block', marginBottom: '4px' }}>
                            {proj.name || 'Project Name'}
                          </strong>
                          {proj.description && (
                            <p style={{ margin: '0 0 4px 0', fontSize: '10px', lineHeight: '1.5' }}>
                              {proj.description}
                            </p>
                          )}
                          {proj.technologies && (
                            <div style={{ fontSize: '9px', color: '#7A9CA3' }}>
                              Technologies: {proj.technologies}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TEMPLATES PAGE
// ============================================================================

const TemplatesPage = ({ onNavigate, isMobile }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const templates = [
    { id: 1, name: 'Modern Professional', category: 'professional', color: '#145A64' },
    { id: 2, name: 'Classic Elegant', category: 'professional', color: '#8B4513' },
    { id: 3, name: 'Minimal Clean', category: 'professional', color: '#2C3E50' },
    { id: 4, name: 'Creative Bold', category: 'creative', color: '#E74C3C' },
    { id: 5, name: 'Tech Startup', category: 'creative', color: '#9B59B6' },
    { id: 6, name: 'Designer Portfolio', category: 'creative', color: '#16A085' },
    { id: 7, name: 'Entry Level', category: 'fresher', color: '#3498DB' },
    { id: 8, name: 'Graduate Fresh', category: 'fresher', color: '#27AE60' },
    { id: 9, name: 'Internship Ready', category: 'fresher', color: '#F39C12' }
  ];

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  return (
    <div style={{
      minHeight: 'calc(100vh - 200px)',
      padding: isMobile ? '40px 20px' : '80px 40px',
      animation: 'fadeIn 0.6s ease'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: isMobile ? '36px' : '48px',
          fontWeight: '700',
          color: '#0D3B42',
          textAlign: 'center',
          marginBottom: '16px',
          fontFamily: '"DM Serif Display", serif'
        }}>
          Resume Templates
        </h1>
        <p style={{
          fontSize: '18px',
          color: '#5A7C82',
          textAlign: 'center',
          marginBottom: '48px',
          maxWidth: '600px',
          margin: '0 auto 48px auto'
        }}>
          Choose from our collection of professionally designed templates
        </p>

        {/* Category Filters */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '48px'
        }}>
          {['all', 'professional', 'creative', 'fresher'].map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                background: selectedCategory === category
                  ? 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)'
                  : 'white',
                color: selectedCategory === category ? 'white' : '#5A7C82',
                border: selectedCategory === category ? 'none' : '2px solid rgba(20, 90, 100, 0.2)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'capitalize',
                boxShadow: selectedCategory === category
                  ? '0 4px 12px rgba(20, 90, 100, 0.25)'
                  : 'none'
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '32px'
        }}>
          {filteredTemplates.map((template, index) => (
            <div
              key={template.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(20, 90, 100, 0.08)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: `slideUp 0.6s ease ${0.1 + index * 0.05}s both`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(20, 90, 100, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
              }}
              onClick={() => onNavigate('builder')}
            >
              <div style={{
                background: `linear-gradient(135deg, ${template.color}15 0%, ${template.color}25 100%)`,
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                position: 'relative'
              }}>
                <div style={{
                  background: 'white',
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: '24px',
                  fontSize: '9px',
                  lineHeight: '1.4',
                  color: '#666'
                }}>
                  <div style={{
                    fontWeight: '700',
                    fontSize: '16px',
                    color: template.color,
                    marginBottom: '8px'
                  }}>
                    Your Name
                  </div>
                  <div style={{
                    height: '2px',
                    background: template.color,
                    width: '50px',
                    marginBottom: '16px'
                  }} />
                  <div style={{ marginBottom: '12px', opacity: '0.7' }}>Professional Summary</div>
                  <div style={{ marginBottom: '8px', opacity: '0.7' }}>Work Experience</div>
                  <div style={{ marginBottom: '8px', opacity: '0.7' }}>Education</div>
                  <div style={{ opacity: '0.7' }}>Skills & Projects</div>
                </div>
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: template.color,
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {template.category}
                </div>
              </div>
              <div style={{ padding: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#0D3B42',
                  margin: '0 0 8px 0'
                }}>
                  {template.name}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#5A7C82',
                  margin: '0 0 16px 0'
                }}>
                  Perfect for {template.category} resumes
                </p>
                <button
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: template.color,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Use Template
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PRIVACY PAGE
// ============================================================================

const PrivacyPage = ({ isMobile }) => {
  return (
    <div style={{
      minHeight: 'calc(100vh - 200px)',
      padding: isMobile ? '40px 20px' : '80px 40px',
      animation: 'fadeIn 0.6s ease'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
            boxShadow: '0 8px 24px rgba(20, 90, 100, 0.25)'
          }}>
            <Shield size={40} color="white" />
          </div>
          <h1 style={{
            fontSize: isMobile ? '36px' : '48px',
            fontWeight: '700',
            color: '#0D3B42',
            marginBottom: '16px',
            fontFamily: '"DM Serif Display", serif'
          }}>
            Privacy Policy
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#5A7C82'
          }}>
            Simple, clear, and honest
          </p>
        </div>

        <div style={{
          background: 'white',
          padding: isMobile ? '32px' : '48px',
          borderRadius: '12px',
          border: '1px solid rgba(20, 90, 100, 0.08)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
        }}>
          <div style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#333'
          }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', fontStyle: 'italic' }}>
              Last Updated: January 3, 2026
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '0'
            }}>
              1. Overview
            </h2>
            <p>
              ResumeX ("we," "our," or "us") is committed to protecting your privacy. This policy explains how we collect, use, and protect your information when you use our resume building and AI analysis services.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              2. Information We Collect
            </h2>
            <p><strong>2.1 Free Resume Builder (No Account Required):</strong></p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Resume Data:</strong> All resume content (personal info, work history, education, skills) is stored locally in your browser using localStorage. We do not have access to this data.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>No Server Upload:</strong> Resume data never leaves your device. All processing happens client-side in your browser.
              </li>
            </ul>

            <p><strong>2.2 PRO Features (Account Required):</strong></p>
            <p>When you sign up for PRO features (AI-powered resume analysis), we collect:</p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Account Information:</strong> Email address, name, and profile picture (if signing in with Google)
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Authentication Data:</strong> Google OAuth tokens (if using Google Sign-In) or encrypted password hash (if using email/password)
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Subscription Data:</strong> Plan type, remaining AI analyzes, purchase history
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>AI Usage Data:</strong> When you use AI analysis, we process your resume text and job descriptions through OpenAI's GPT-4o. This data is sent to OpenAI solely for analysis and is not permanently stored by us.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Payment Information:</strong> Processed securely through Razorpay. We do not store your credit card details.
              </li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              3. How We Use Your Information
            </h2>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Authentication:</strong> To verify your identity and provide access to PRO features
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Service Delivery:</strong> To process AI resume analysis requests and track usage credits
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Payment Processing:</strong> To handle purchases and maintain transaction records
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Customer Support:</strong> To respond to your inquiries and provide assistance
              </li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              4. Data Storage and Security
            </h2>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Database:</strong> User accounts and subscription data are stored in Cloudflare D1 database with industry-standard encryption
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Resume Content:</strong> We do not store your actual resume content on our servers. AI analysis is performed in real-time and not retained.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Secure Transmission:</strong> All data is transmitted over HTTPS with TLS encryption
              </li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              5. Third-Party Services
            </h2>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Google OAuth:</strong> For authentication. See Google's Privacy Policy at https://policies.google.com/privacy
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>OpenAI GPT-4o:</strong> For AI resume analysis. Resume text is sent to OpenAI for processing. See OpenAI's Privacy Policy at https://openai.com/privacy
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Razorpay:</strong> For payment processing. See Razorpay's Privacy Policy at https://razorpay.com/privacy
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Cloudflare:</strong> For hosting and database services. See Cloudflare's Privacy Policy at https://www.cloudflare.com/privacypolicy
              </li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              6. Data Sharing
            </h2>
            <p>
              We do not sell, rent, or share your personal information with third parties except:
            </p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>With service providers (Google, OpenAI, Razorpay, Cloudflare) as necessary to provide our services</li>
              <li style={{ marginBottom: '12px' }}>When required by law or to protect our legal rights</li>
              <li style={{ marginBottom: '12px' }}>With your explicit consent</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              7. Your Rights
            </h2>
            <p>You have the right to:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>Access your account information</li>
              <li style={{ marginBottom: '12px' }}>Request deletion of your account and associated data</li>
              <li style={{ marginBottom: '12px' }}>Export your transaction history</li>
              <li style={{ marginBottom: '12px' }}>Opt out of marketing communications</li>
            </ul>
            <p>To exercise these rights, contact us at info.theresumex@gmail.com</p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              8. Browser Data and Local Storage
            </h2>
            <p>
              For the free resume builder, your resume data is stored locally in your browser. <strong>Clearing your browser's cache or data will delete your resume.</strong> We recommend:
            </p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>Regularly downloading your resume as a PDF backup</li>
              <li style={{ marginBottom: '12px' }}>Being careful when clearing browser data</li>
              <li style={{ marginBottom: '12px' }}>Using the same browser and device to access your saved work</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              9. Children's Privacy
            </h2>
            <p>
              Our services are not directed to individuals under 16. We do not knowingly collect personal information from children under 16. If you become aware that a child has provided us with personal information, please contact us.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              10. International Data Transfers
            </h2>
            <p>
              Your data may be processed in countries outside your country of residence, including the United States, where our service providers (Cloudflare, OpenAI, Google) operate. We ensure appropriate safeguards are in place for such transfers.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              11. Changes to This Policy
            </h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              12. Contact Us
            </h2>
            <p style={{ marginBottom: '0' }}>
              If you have questions about this privacy policy or our data practices, please contact us at:
              <br /><br />
              <strong>Email:</strong> info.theresumex@gmail.com<br />
              <strong>Service:</strong> ResumeX<br />
              <strong>Website:</strong> https://theresumex.cv
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TERMS OF SERVICE PAGE
// ============================================================================

const TermsPage = ({ isMobile }) => {
  return (
    <div style={{
      minHeight: 'calc(100vh - 200px)',
      padding: isMobile ? '40px 20px' : '80px 40px',
      animation: 'fadeIn 0.6s ease'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
            boxShadow: '0 8px 24px rgba(20, 90, 100, 0.25)'
          }}>
            <FileText size={40} color="white" />
          </div>
          <h1 style={{
            fontSize: isMobile ? '36px' : '48px',
            fontWeight: '700',
            color: '#0D3B42',
            marginBottom: '16px',
            fontFamily: '"DM Serif Display", serif'
          }}>
            Terms of Service
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#5A7C82'
          }}>
            Clear terms for our services
          </p>
        </div>

        <div style={{
          background: 'white',
          padding: isMobile ? '32px' : '48px',
          borderRadius: '12px',
          border: '1px solid rgba(20, 90, 100, 0.08)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
        }}>
          <div style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#333'
          }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', fontStyle: 'italic' }}>
              Last Updated: January 3, 2026
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '0'
            }}>
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using ResumeX ("Service," "we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              2. Description of Service
            </h2>
            <p>ResumeX provides:</p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Free Resume Builder:</strong> A client-side resume building tool with local storage
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>ATS Checker:</strong> Free ATS compatibility analysis for resumes
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>PRO Features:</strong> AI-powered resume analysis and improvement suggestions using GPT-4o (paid service)
              </li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              3. User Accounts and Authentication
            </h2>
            <p><strong>3.1 Account Creation:</strong></p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>Free resume builder does not require an account</li>
              <li style={{ marginBottom: '12px' }}>PRO features require account creation via Google OAuth or email/password</li>
              <li style={{ marginBottom: '12px' }}>You must provide accurate and complete information</li>
              <li style={{ marginBottom: '12px' }}>You are responsible for maintaining the security of your account</li>
            </ul>

            <p><strong>3.2 Account Responsibilities:</strong></p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>You must be at least 16 years old to create an account</li>
              <li style={{ marginBottom: '12px' }}>You may not share your account credentials</li>
              <li style={{ marginBottom: '12px' }}>You are responsible for all activities under your account</li>
              <li style={{ marginBottom: '12px' }}>Notify us immediately of any unauthorized access</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              4. PRO Subscription and Payments
            </h2>
            <p><strong>4.1 Pricing and Credits:</strong></p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>PRO features are sold as AI analysis credits (not recurring subscriptions)</li>
              <li style={{ marginBottom: '12px' }}>Prices are displayed in USD and INR before purchase</li>
              <li style={{ marginBottom: '12px' }}>Credits never expire</li>
              <li style={{ marginBottom: '12px' }}>Each AI analysis consumes one credit</li>
            </ul>

            <p><strong>4.2 Payment Processing:</strong></p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>Payments are processed securely through Razorpay</li>
              <li style={{ marginBottom: '12px' }}>We do not store your payment card information</li>
              <li style={{ marginBottom: '12px' }}>All sales are processed in real-time</li>
              <li style={{ marginBottom: '12px' }}>You will receive a confirmation upon successful payment</li>
            </ul>

            <p><strong>4.3 Refund Policy:</strong></p>
            <p>Refunds are only applicable in the following cases:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>Service not received - Payment completed but credits were not added to your account</li>
              <li style={{ marginBottom: '12px' }}>Technical issue prevented use - System errors or bugs prevented you from using purchased credits</li>
              <li style={{ marginBottom: '12px' }}>Duplicate charges - You were charged multiple times for the same purchase</li>
            </ul>
            <p style={{ marginTop: '16px' }}>Refund process:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>Request refunds within 7 days of purchase</li>
              <li style={{ marginBottom: '12px' }}>Refunds are processed to the original payment method within 5-10 business days</li>
              <li style={{ marginBottom: '12px' }}>To request a refund, contact info.theresumex@gmail.com with your payment ID and reason</li>
            </ul>
            <p style={{ marginTop: '16px', fontStyle: 'italic', fontSize: '14px', color: '#666' }}>
              Note: Refunds are not provided for change of mind or after credits have been successfully used for AI analysis.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              5. Acceptable Use
            </h2>
            <p>You agree not to:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>Use the Service for any illegal purpose</li>
              <li style={{ marginBottom: '12px' }}>Attempt to gain unauthorized access to our systems</li>
              <li style={{ marginBottom: '12px' }}>Reverse engineer or attempt to extract source code</li>
              <li style={{ marginBottom: '12px' }}>Use automated systems to access the Service without permission</li>
              <li style={{ marginBottom: '12px' }}>Resell or redistribute our services</li>
              <li style={{ marginBottom: '12px' }}>Share account credentials or transfer credits to others</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              6. Intellectual Property
            </h2>
            <p><strong>6.1 Your Content:</strong></p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>You retain all rights to your resume content and personal information</li>
              <li style={{ marginBottom: '12px' }}>We do not claim ownership of your resume data</li>
              <li style={{ marginBottom: '12px' }}>Resume content stored locally never becomes our property</li>
            </ul>

            <p><strong>6.2 Our Content:</strong></p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>The Service, including all software, design, and templates, is owned by ResumeX</li>
              <li style={{ marginBottom: '12px' }}>You may not copy, modify, or distribute our software or design</li>
              <li style={{ marginBottom: '12px' }}>AI-generated suggestions become your property once generated</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              7. Service Availability and Modifications
            </h2>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>We strive for 99.9% uptime but do not guarantee uninterrupted service</li>
              <li style={{ marginBottom: '12px' }}>We may modify, suspend, or discontinue features with notice</li>
              <li style={{ marginBottom: '12px' }}>We may perform maintenance that temporarily affects service availability</li>
              <li style={{ marginBottom: '12px' }}>Unused credits remain valid even if features are modified</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              8. Disclaimers and Limitations of Liability
            </h2>
            <p><strong>8.1 Service "As Is":</strong></p>
            <p>
              The Service is provided "as is" without warranties of any kind. We do not guarantee that AI-generated suggestions will result in job offers or improved resume performance.
            </p>

            <p style={{ marginTop: '16px' }}><strong>8.2 Limitation of Liability:</strong></p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>We are not liable for any indirect, incidental, or consequential damages</li>
              <li style={{ marginBottom: '12px' }}>Our total liability is limited to the amount you paid in the last 12 months</li>
              <li style={{ marginBottom: '12px' }}>We are not responsible for data loss due to browser cache clearing</li>
              <li style={{ marginBottom: '12px' }}>We are not liable for third-party service interruptions (OpenAI, Razorpay, etc.)</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              9. Data and Privacy
            </h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy. Please review it at theresumex.cv/privacy to understand our data practices.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              10. Termination
            </h2>
            <p><strong>10.1 By You:</strong></p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>You may stop using the Service at any time</li>
              <li style={{ marginBottom: '12px' }}>You may request account deletion by contacting us</li>
              <li style={{ marginBottom: '12px' }}>Unused credits are forfeited upon account deletion</li>
            </ul>

            <p><strong>10.2 By Us:</strong></p>
            <ul style={{ paddingLeft: '24px' }}>
              <li style={{ marginBottom: '12px' }}>We may terminate accounts that violate these terms</li>
              <li style={{ marginBottom: '12px' }}>We may suspend access for non-payment or fraudulent activity</li>
              <li style={{ marginBottom: '12px' }}>We will provide notice before termination unless prohibited by law</li>
            </ul>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              11. Dispute Resolution
            </h2>
            <p><strong>11.1 Governing Law:</strong></p>
            <p>
              These Terms are governed by the laws of India. Any disputes will be resolved in the courts of India.
            </p>

            <p style={{ marginTop: '16px' }}><strong>11.2 Arbitration:</strong></p>
            <p>
              For disputes under $5,000 USD, we encourage informal resolution by contacting us first. We'll work in good faith to resolve issues quickly.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              12. Changes to Terms
            </h2>
            <p>
              We may update these Terms from time to time. Material changes will be communicated via email (for account holders) or via notice on the website. Continued use after changes constitutes acceptance.
            </p>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              13. Contact Information
            </h2>
            <p style={{ marginBottom: '0' }}>
              For questions about these Terms, please contact us:
              <br /><br />
              <strong>Email:</strong> info.theresumex@gmail.com<br />
              <strong>Service:</strong> ResumeX<br />
              <strong>Website:</strong> https://theresumex.cv
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ABOUT PAGE
// ============================================================================

const AboutPage = ({ isMobile }) => {
  return (
    <div style={{
      minHeight: 'calc(100vh - 200px)',
      padding: isMobile ? '40px 20px' : '80px 40px',
      animation: 'fadeIn 0.6s ease'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: isMobile ? '36px' : '48px',
          fontWeight: '700',
          color: '#0D3B42',
          textAlign: 'center',
          marginBottom: '16px',
          fontFamily: '"DM Serif Display", serif'
        }}>
          About theresumex
        </h1>
        <p style={{
          fontSize: '18px',
          color: '#5A7C82',
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          Built for privacy, designed for simplicity
        </p>

        <div style={{
          background: 'white',
          padding: isMobile ? '32px' : '48px',
          borderRadius: '12px',
          border: '1px solid rgba(20, 90, 100, 0.08)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          marginBottom: '32px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#145A64',
            marginBottom: '16px',
            marginTop: '0'
          }}>
            Why We Built This
          </h2>
          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '16px' }}>
            In a world where every service seems to require an account, collect your data, and store it in "the cloud," we wanted to build something different.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '16px' }}>
            theresumex was created with a simple mission: <strong>give people a professional tool to build resumes without compromising their privacy</strong>. Your resume contains sensitive personal information—your work history, education, contact details, and more. This data should be yours alone.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '0' }}>
            We believe privacy shouldn't be a premium feature. It should be the default.
          </p>
        </div>

        <div style={{
          background: 'white',
          padding: isMobile ? '32px' : '48px',
          borderRadius: '12px',
          border: '1px solid rgba(20, 90, 100, 0.08)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          marginBottom: '32px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#145A64',
            marginBottom: '16px',
            marginTop: '0'
          }}>
            How It's Different
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              {
                title: 'Zero Data Collection',
                description: 'Unlike traditional resume builders, we literally cannot see your data. Everything stays in your browser.'
              },
              {
                title: 'No Vendor Lock-In',
                description: 'Export your resume as a PDF anytime. No premium tiers, no artificial limitations, no forced upgrades.'
              },
              {
                title: 'Open and Transparent',
                description: 'Our approach is straightforward: client-side processing, local storage, and nothing else.'
              },
              {
                title: 'Works Offline',
                description: 'After the initial load, you can build your resume even without an internet connection.'
              }
            ].map((item, index) => (
              <div key={index}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#0D3B42',
                  marginBottom: '8px',
                  marginTop: '0'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: '#5A7C82',
                  margin: '0'
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
          padding: isMobile ? '32px' : '48px',
          borderRadius: '12px',
          color: 'white',
          textAlign: 'center'
        }}>
          <Heart size={48} style={{ marginBottom: '16px', opacity: '0.9' }} />
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            marginBottom: '12px',
            marginTop: '0'
          }}>
            Built with Care
          </h2>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.6',
            opacity: '0.9',
            margin: '0'
          }}>
            theresumex is a passion project created to demonstrate that powerful tools can respect user privacy. We hope it helps you in your career journey.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// FAQ PAGE
// ============================================================================

const FAQPage = ({ isMobile }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Is my resume uploaded anywhere?',
      answer: 'No. Your resume is stored entirely in your browser using localStorage. It never touches our servers or any cloud storage. We have no way to access your data.'
    },
    {
      question: 'Can you see my data?',
      answer: 'Absolutely not. Since all processing happens in your browser and nothing is transmitted to any server, we have zero visibility into your resume content. We couldn\'t access it even if we wanted to.'
    },
    {
      question: 'What happens if I refresh the page?',
      answer: 'Your data is automatically saved in your browser as you work. Refreshing the page will load your previously saved resume. However, clearing your browser data will delete it, so we recommend downloading your resume as a PDF backup.'
    },
    {
      question: 'Does this work offline?',
      answer: 'After the initial load, yes! Since all processing happens in your browser, you can continue editing your resume even without an internet connection. PDF generation also works offline.'
    },
    {
      question: 'Is it free?',
      answer: 'Yes, theresumex is completely free to use. All features are available to everyone with no premium tiers or hidden costs.'
    },
    {
      question: 'How do I access my resume on another device?',
      answer: 'Since data is stored locally in your browser, you can\'t automatically sync across devices. However, you can download your resume as a PDF and re-enter the data on another device, or copy the data manually.'
    },
    {
      question: 'What if I clear my browser data?',
      answer: 'Clearing your browser\'s cache or data will delete your locally stored resume. We strongly recommend regularly downloading your resume as a PDF to have a backup.'
    },
    {
      question: 'How is the PDF generated?',
      answer: 'PDFs are generated entirely in your browser using JavaScript libraries. Your resume content never leaves your device during the export process.'
    },
    {
      question: 'Do you use analytics or tracking?',
      answer: 'We may use basic, anonymous analytics to understand how people use the site (like page views), but we never collect any personal information or resume content.'
    },
    {
      question: 'Can I request my data be deleted?',
      answer: 'Since we don\'t store any of your data on our servers, there\'s nothing for us to delete. Your data exists only on your device, and you can delete it anytime by clearing your browser data.'
    }
  ];

  return (
    <div style={{
      minHeight: 'calc(100vh - 200px)',
      padding: isMobile ? '40px 20px' : '80px 40px',
      animation: 'fadeIn 0.6s ease'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: isMobile ? '36px' : '48px',
          fontWeight: '700',
          color: '#0D3B42',
          textAlign: 'center',
          marginBottom: '16px',
          fontFamily: '"DM Serif Display", serif'
        }}>
          Frequently Asked Questions
        </h1>
        <p style={{
          fontSize: '18px',
          color: '#5A7C82',
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          Everything you need to know about theresumex
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(20, 90, 100, 0.08)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                style={{
                  width: '100%',
                  padding: isMobile ? '20px' : '24px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <span style={{
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  color: '#0D3B42'
                }}>
                  {faq.question}
                </span>
                <span style={{
                  fontSize: '24px',
                  color: '#145A64',
                  transition: 'transform 0.3s ease',
                  transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)',
                  flexShrink: 0
                }}>
                  +
                </span>
              </button>
              {openIndex === index && (
                <div style={{
                  padding: isMobile ? '0 20px 20px 20px' : '0 24px 24px 24px',
                  fontSize: '15px',
                  lineHeight: '1.7',
                  color: '#5A7C82',
                  animation: 'slideDown 0.3s ease'
                }}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '60px',
          padding: isMobile ? '32px' : '40px',
          background: 'linear-gradient(135deg, #F0F9FA 0%, #E8F4F5 100%)',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px solid rgba(20, 90, 100, 0.08)'
        }}>
          <h3 style={{
            fontSize: '22px',
            fontWeight: '600',
            color: '#145A64',
            marginBottom: '12px',
            marginTop: '0'
          }}>
            Still have questions?
          </h3>
          <p style={{
            fontSize: '16px',
            color: '#5A7C82',
            marginBottom: '24px'
          }}>
            We're happy to help clarify anything about how theresumex works
          </p>
          <button
            style={{
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(20, 90, 100, 0.2)'
            }}
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

export default function App() {
  const [currentPage, setCurrentPage] = useState(() => {
    // Initialize page from URL path
    const path = window.location.pathname.substring(1); // Remove leading slash
    return path || 'home';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [hasProSubscription, setHasProSubscription] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Handle browser back/forward buttons
    const handlePopState = () => {
      const path = window.location.pathname.substring(1);
      setCurrentPage(path || 'home');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Handle OAuth callback - capture auth_token and user from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');
    const userParam = urlParams.get('user');

    if (authToken && userParam) {
      console.log('OAuth callback detected, storing credentials...');
      // Store auth data in localStorage
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('user_info', userParam);

      // Check subscription and redirect accordingly
      const checkSubscriptionAndRedirect = async () => {
        try {
          const response = await fetch('https://theresumex.cv/api/user/subscription', {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const data = await response.json();

          // Clean up URL (remove query parameters)
          window.history.replaceState({}, '', window.location.pathname);

          if (data.success && data.subscription && data.subscription.remainingAnalyzes > 0) {
            // User has credits, redirect to AI Analyzer
            console.log('User has credits, redirecting to AI Analyzer');
            setHasProSubscription(true);
            navigateToPage('ai-analyzer');
          } else {
            // No credits, redirect to Pricing
            console.log('User has no credits, redirecting to Pricing');
            setHasProSubscription(false);
            navigateToPage('pricing');
          }
        } catch (err) {
          console.error('Failed to check subscription:', err);
          // On error, clean URL and redirect to pricing
          window.history.replaceState({}, '', window.location.pathname);
          navigateToPage('pricing');
        }
      };

      checkSubscriptionAndRedirect();
    }
  }, []); // Run once on mount

  useEffect(() => {
    // Check if user is logged in and has PRO subscription
    const checkProStatus = async () => {
      const token = localStorage.getItem('auth_token');
      const userInfo = localStorage.getItem('user_info');

      if (token && userInfo) {
        console.log('User logged in:', JSON.parse(userInfo));

        try {
          // Check subscription status
          const response = await fetch('https://theresumex.cv/api/user/subscription', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();

          if (data.success && data.subscription && data.subscription.remainingAnalyzes > 0) {
            setHasProSubscription(true);
          } else {
            setHasProSubscription(false);
          }
        } catch (err) {
          console.error('Failed to check PRO status:', err);
          setHasProSubscription(false);
        }
      } else {
        setHasProSubscription(false);
      }
    };

    checkProStatus();
  }, [currentPage]); // Re-check when page changes

  const handleAuthSuccess = async (userData, token) => {
    // Store user data
    console.log('Auth success:', userData);

    // Check subscription status and redirect accordingly
    try {
      const response = await fetch('https://theresumex.cv/api/user/subscription', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.subscription && data.subscription.remainingAnalyzes > 0) {
        // User has credits, redirect to AI Analyzer
        console.log('User has credits, redirecting to AI Analyzer');
        setHasProSubscription(true);
        navigateToPage('ai-analyzer');
      } else {
        // No credits, redirect to Pricing
        console.log('User has no credits, redirecting to Pricing');
        setHasProSubscription(false);
        navigateToPage('pricing');
      }
    } catch (err) {
      console.error('Failed to check subscription:', err);
      // On error, redirect to pricing
      navigateToPage('pricing');
    }
  };

  const navigateToPage = (page) => {
    setCurrentPage(page);
    // Update URL without page reload
    const newPath = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', newPath);
    // Scroll to top when navigating
    window.scrollTo(0, 0);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={navigateToPage} isMobile={isMobile} />;
      case 'builder':
        return <BuilderPage isMobile={isMobile} />;
      case 'ats-checker':
        return <ATSChecker isMobile={isMobile} onNavigate={navigateToPage} onOpenAuth={() => setAuthModalOpen(true)} />;
      case 'ai-analyzer':
        return <AIAnalyzer isMobile={isMobile} onOpenAuth={() => setAuthModalOpen(true)} onNavigate={navigateToPage} />;
      case 'pricing':
        return <PricingPage isMobile={isMobile} onOpenAuth={() => setAuthModalOpen(true)} onNavigate={navigateToPage} />;
      case 'templates':
        return <TemplatesPage onNavigate={navigateToPage} isMobile={isMobile} />;
      case 'privacy':
        return <PrivacyPage isMobile={isMobile} />;
      case 'terms':
        return <TermsPage isMobile={isMobile} />;
      case 'about':
        return <AboutPage isMobile={isMobile} />;
      case 'faq':
        return <FAQPage isMobile={isMobile} />;
      default:
        return <HomePage onNavigate={navigateToPage} isMobile={isMobile} />;
    }
  };

  return (
    <div style={{
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      minHeight: '100vh',
      background: '#FFFFFF'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@400;500;600;700&display=swap');

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        input:focus,
        textarea:focus,
        button:focus {
          outline: 2px solid #1A8A99;
          outline-offset: 2px;
        }

        input,
        textarea {
          font-family: inherit;
        }

        button {
          font-family: inherit;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: #1A8A99;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #145A64;
        }
      `}</style>

      <Navigation
        currentPage={currentPage}
        onNavigate={navigateToPage}
        isMobile={isMobile}
        hasProSubscription={hasProSubscription}
      />

      {renderPage()}

      <Footer onNavigate={navigateToPage} isMobile={isMobile} />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </div>
  );
}
